/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import * as React from 'react';
import Footer from '../components/custom/navigation/Footer';
import { OptionsConfig } from '../components/custom/modals/OptionsModal';
import { Container } from 'react-grid-system';
import { isMobileOnly } from 'react-device-detect';
import { ModalType } from '../components/custom/modals/Modals';
import MainHeader from '../components/custom/navigation/MainHeader';
import MeasurementsBlock from '../components/custom/blocks/MeasurementsBlock';
import ConfigurationHeader from '../components/custom/blocks/ConfigurationHeader';
import ConfiguratorContent from '../components/custom/blocks/ConfiguratorContent';
import {
  initialConfiguration,
  initialProbeData,
  initialTempCompProbeData,
  ProbeProps,
  initialTransmitterData,
  TransmitterProps,
  initialAttachmentData,
  initialSelections,
  filterProbesByMeasurements,
} from '../components/custom/configuration/ConfigurationFunctions';
import { setStorageFromParams } from '../components/custom/configuration/LocalStorage';
import getUrlParams from '../components/custom/configuration/getUrlParams';
import { Grid } from '@vaisala-rockhopper/components';
import SelectSettingsBlock from '../components/custom/blocks/SelectSettingsBlock';

interface LandingPageProps {
  onHelpOpen: VoidFunction;
  onOptionsOpen: (title: string, config: OptionsConfig) => void;
  themeIndex: number;
  theme: any;
  setIsScrolled: (value: boolean) => void;
  openModal: (type: ModalType, title?: string, optionsConfig?: OptionsConfig, onAccept?: VoidFunction, onReject?: VoidFunction) => void;
}

const NewLandingPage = ({ openModal }: LandingPageProps) => {
  const [loadedSavedConfiguration, setLoadedSavedConfiguration] = React.useState(false);
  const [filteredProbes, setFilteredProbes] = React.useState<ProbeProps[]>(initialProbeData);
  const [filteredSecondaryProbes, setFilteredSecondayProbes] = React.useState<ProbeProps[]>([]);
  const [tempCompProbe, setTempCompProbe] = React.useState<ProbeProps[]>(initialTempCompProbeData);
  const [allTransmitters, setAllTransmitters] = React.useState<TransmitterProps[]>(initialTransmitterData);
  const [filteredTransmitters, setFilteredTransmitters] = React.useState<TransmitterProps[]>([]);
  const [probeAttachments, setProbeAttachments] = React.useState<ProbeProps[]>(initialAttachmentData);
  const [isConfigurationReady, setIsConfigurationReady] = React.useState(true);
  const [showConfiguration, setShowConfiguration] = React.useState(true);

  const [transmitterType, setTransmitterType] = React.useState('handheld');

  const [selectedMeasurements, setSelectedMeasurements] = React.useState<string[]>(() => {
    const url = new URL(window.location.href);
    const urlparams = Object.fromEntries(new URLSearchParams(url.search));
    let savedConfiguration: string[] = [];
    if (Object.keys(urlparams).length > 0 && Object.keys(urlparams).includes('c')) {
      let paramsOnLoad = getUrlParams(url);
      savedConfiguration = paramsOnLoad['conf_values'];
      setStorageFromParams('selected_measurements', JSON.stringify(paramsOnLoad['conf_values']));
      setLoadedSavedConfiguration(true);
      if (paramsOnLoad['sel_values']['transmitter' as keyof typeof paramsOnLoad['sel_values']] !== 'Indigo80') {
        setTransmitterType('fixed');
      }
    } else {
      savedConfiguration = JSON.parse(localStorage.getItem('selected_measurements') ?? 'false');
      if (savedConfiguration) {
        setLoadedSavedConfiguration(true);
      } else {
        setStorageFromParams('selected_measurements', [JSON.stringify([])]);
      }
    }
    return savedConfiguration || [];
  });

  const onMeasurementSelectChange = React.useCallback(
    (selectParam: string, paramValue: boolean) => {
      let selectedMeasurementsCopy = [...selectedMeasurements];
      if (!paramValue) {
        if (selectedMeasurementsCopy.includes(selectParam)) {
          selectedMeasurementsCopy = selectedMeasurementsCopy.filter((e) => e !== selectParam);
        }
      } else {
        selectedMeasurementsCopy.push(selectParam);
      }
      if (selectedMeasurementsCopy.length === 0) {
        let filteredProbes = initialProbeData();
        filteredProbes = filteredProbes.filter((o: any) => {
            return o.probe_type[transmitterType] === true
        })
        setFilteredProbes(filteredProbes);
        setTempCompProbe(initialTempCompProbeData);
        setStorageFromParams('product_selections', JSON.stringify(initialSelections()));
      }
      selectedMeasurementsCopy.sort();
      setSelectedMeasurements(selectedMeasurementsCopy);
      setStorageFromParams('selected_measurements', JSON.stringify(selectedMeasurementsCopy));
    },
    [selectedMeasurements]
  );

  React.useEffect(() => {
    if (allTransmitters) {
      setFilteredTransmitters(allTransmitters)
    }
  }, [allTransmitters])

  const onResetConfiguration = () => {
    setFilteredProbes(initialProbeData);
    setAllTransmitters(initialTransmitterData);
    setTempCompProbe(initialTempCompProbeData);
    setProbeAttachments(initialAttachmentData);
    setTransmitterType('handheld')
    setSelectedMeasurements([]);
    setStorageFromParams('selected_transmitter', null);
    setStorageFromParams('product_selections', JSON.stringify(initialSelections()));
    setStorageFromParams('center_anchor', JSON.stringify([]));
    setStorageFromParams('selected_measurements', JSON.stringify([]));
    setIsConfigurationReady(true);
    setShowConfiguration(true);
  };

  const onShowConfiguration = () => {
    setShowConfiguration(true);
  };

  React.useEffect(() => {
    let newFilteredTransmitters = [...allTransmitters];
    newFilteredTransmitters = newFilteredTransmitters.filter((transmitter) => transmitter.transmitter_type[transmitterType] === true);
    setFilteredTransmitters(newFilteredTransmitters);
    if (transmitterType === "handheld") {
      let selectedMeasurementsCopy = [...selectedMeasurements];
      if (selectedMeasurementsCopy.includes('barometric_pressure')) {
        selectedMeasurementsCopy = selectedMeasurementsCopy.filter(measurement => measurement !== "barometric_pressure")
        setSelectedMeasurements(selectedMeasurementsCopy);
        setStorageFromParams('selected_measurements', JSON.stringify(selectedMeasurementsCopy));
      }
    }
  }, [transmitterType, selectedMeasurements])

  React.useEffect(() => {
    if (filteredSecondaryProbes.length > 0) {
      let newFilteredTransmitters = [...filteredTransmitters]
      newFilteredTransmitters = newFilteredTransmitters.filter((transmitter) => transmitter.dual_probe === true);
      setFilteredTransmitters(newFilteredTransmitters)
    }
  }, [filteredSecondaryProbes])

  React.useEffect(() => {
    let selectedMeasurementsCopy = [...selectedMeasurements];
    let newFilteredTransmitters = [...allTransmitters];
    if (transmitterType === "fixed" && selectedMeasurementsCopy.includes('barometric_pressure')) {
      newFilteredTransmitters = newFilteredTransmitters.filter((transmitter) => {
        if (transmitter !== undefined) {
          return transmitter.measures?.includes('barometric_pressure');
        } else {
          return null;
        }
      });
      setFilteredTransmitters(newFilteredTransmitters);
      selectedMeasurementsCopy = selectedMeasurementsCopy.filter((val) => val !== 'barometric_pressure');
    }
    const filteredByMeasurements = filterProbesByMeasurements(selectedMeasurementsCopy, transmitterType);
    setFilteredProbes(filteredByMeasurements.probes);
    if (filteredByMeasurements.secondary_probes.length > 0) {
      setFilteredSecondayProbes(filteredByMeasurements.secondary_probes);
    } else {
      setFilteredSecondayProbes([]);
    }
    if (selectedMeasurementsCopy.length > 0 && filteredByMeasurements.probes.length > 0) {
      setIsConfigurationReady(true);
      setShowConfiguration(true);
      if (loadedSavedConfiguration) {
        setShowConfiguration(true);
        setLoadedSavedConfiguration(false);
      }
    } else {
      if (newFilteredTransmitters.length > 0) {
        if (selectedMeasurementsCopy.length > 0) {
          setIsConfigurationReady(false);
          setShowConfiguration(false);
        } else {
          setIsConfigurationReady(true);
          setShowConfiguration(true);
        }

      } else {
        setIsConfigurationReady(false);
        setShowConfiguration(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMeasurements, transmitterType]);

  return (
    <Container className={`${isMobileOnly ? 'container--mobile' : 'container--desktop'}`}>
      <MainHeader />

      <div className="selector-content-container">
        <Grid>
          <Grid.Row>
            <Grid.Col xl={3}>
              <SelectSettingsBlock
                onResetConfiguration={onResetConfiguration}
                selectedMeasurements={selectedMeasurements}
                onMeasurementSelectChange={onMeasurementSelectChange}
                transmitterType={transmitterType}
                setTransmitterType={setTransmitterType}
              ></SelectSettingsBlock>
              {/* <MeasurementsBlock
                onResetConfiguration={onResetConfiguration}
                selectedMeasurements={selectedMeasurements}
                onMeasurementSelectChange={onMeasurementSelectChange}
              /> */}
            </Grid.Col>
            {/* <Grid.Col xl={6}>
              <ConfigurationHeader
                openModal={openModal}
                onShowConfiguration={onShowConfiguration}
                isConfigurationReady={isConfigurationReady}
                showConfiguration={showConfiguration}
              />
            </Grid.Col> */}
            <Grid.Col xl={9}>
              <ConfiguratorContent
                showConfiguration={showConfiguration}
                isConfigurationReady={isConfigurationReady}
                filteredProbes={filteredProbes}
                filteredSecondaryProbes={filteredSecondaryProbes}
                tempCompProbe={tempCompProbe}
                filteredTransmitters={filteredTransmitters}
                selectedMeasurements={selectedMeasurements}
                probeAttachments={probeAttachments}
                loadedSavedConfiguration={loadedSavedConfiguration}
                onLoadedSavedConfigurationChange={setLoadedSavedConfiguration}
                transmitterType={transmitterType}
                setTransmitterType={setTransmitterType}
                openModal={openModal}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
        {/* <ConditionsBlock
         onConfSelectChange={onConfSelectChange}
         selectedConfiguration={selectedConfiguration}
         conditionsAvailable={conditionsAvailable}
         conditionOptionsAvailable={conditionOptionsAvailable}
      /> */}
      </div>
      <div className={`banner-wrap ${isMobileOnly ? 'banner-wrap--mobile' : 'banner-wrap--desktop'}`}>
        <div className="gtm-content gtm-content--landing-page" />
      </div>
      <Footer />
    </Container>
  );
};
export default NewLandingPage;
